'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import {
  Award, Download, CheckCircle, Loader, Clock,
  XCircle, Eye, RefreshCw, MessageCircle, AlertCircle, Mail, User,
  ChevronRight, Circle, Lock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/lib/context/ToastContext';
import { analytics } from '@/lib/analytics';
import { enrollmentRequiresTest } from '@/lib/utils/ticket-permissions';
import type { CertificateStatus } from '@/lib/certificates/certificateStatus.server';
import { isWithinUpgradeWindow, getUpgradeDeadline } from '@/lib/utils/upgrade-window';
import { formatDateTimeEuropean, formatShortDateTime } from '@/lib/utils/date';
import { resolveWindow, windowStatus } from '@/lib/availability/resolveWindow';
import type { EventTimes, WindowInput } from '@/lib/availability/types';
import { useTimeBoundaries } from '@/lib/hooks/useTimeBoundaries';
import { hasLiveQuestions } from '@/lib/surveys/liveQuestions';

type CertificateType = 'participant' | 'speaker';

interface CertificatesTabProps {
  eventId: string;
  enrollmentId: string;
  eventState: 'future' | 'live' | 'past';
  eventEndDate?: string | null;
  onOpenCertificateModal: (certificateType?: CertificateType, regenerate?: boolean) => void;
  onUpgradeTicket?: () => void;
  refreshKey?: number;
}

interface Certificate {
  id: string;
  certificate_number: string;
  certificate_url: string | null;
  verification_code: string | null;
  issued_at: string | null;
  status: string | null;
}

interface RequirementsStatus {
  tests: {
    title: string;
    passed: boolean;
    expired: boolean;
    // Not open yet: shown as "opens at …", never as a failure.
    upcoming: boolean;
    opensAt: string | null;
    closesAt: string | null;
  }[];
  surveys: { title: string; completed: boolean }[];
  allTestsPassed: boolean;
  allSurveysCompleted: boolean;
}

export default function CertificatesTab({
  eventId,
  enrollmentId,
  eventState,
  eventEndDate,
  onOpenCertificateModal,
  onUpgradeTicket,
  refreshKey,
}: CertificatesTabProps) {
  const { t, language } = useLanguage();
  const toast = useToast();
  const supabase = useMemo(() => createClient(), []);

  const [certificate, setCertificate] = useState<Certificate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  // Whether any ticket on this event grants a certificate at all. Only meaningful
  // in the no-access state, where it gates the upgrade CTA.
  const [hasUpgradeTarget, setHasUpgradeTarget] = useState(false);
  const [status, setStatus] = useState<CertificateStatus | null>(null);
  const [selectedType, setSelectedType] = useState<CertificateType>('participant');
  const [requirements, setRequirements] = useState<RequirementsStatus | null>(null);
  const [downloading, setDownloading] = useState(false);

  const [eventTitle, setEventTitle] = useState('');

  // Report template state
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportStep, setReportStep] = useState<'regenerate' | 'notify'>('regenerate');
  const [reportSending, setReportSending] = useState(false);
  const [organizerInfo, setOrganizerInfo] = useState<{ name: string; email: string } | null>(null);
  const [organizerLoading, setOrganizerLoading] = useState(false);

  // ── Data fetching ──────────────────────────────────────────────

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);

    try {
      // 0. Fetch event title (needed for report template)
      const { data: titleData } = await supabase
        .from('content')
        .select('title, start_date, end_date')
        .eq('id', eventId)
        .single();
      if (titleData?.title) setEventTitle(titleData.title);
      const eventTimes: EventTimes = {
        start_date: titleData?.start_date ?? null,
        end_date: titleData?.end_date ?? eventEndDate ?? null,
      };

      // 0b. The participant's own certificate status (config + render), decided
      //     server-side from THEIR ticket – never "some ticket on the event".
      const stateRes = await fetch(`/api/certificates/state?contentIds=${eventId}`);
      if (!stateRes.ok) throw new Error(`certificate state ${stateRes.status}`);
      const { statuses } = (await stateRes.json()) as { statuses: Record<string, CertificateStatus> };
      const own = statuses[eventId] ?? null;
      setStatus(own);
      if (own?.templateType) setSelectedType(own.templateType);

      // 1. Check for existing certificate
      const { data: existingCert } = await supabase
        .from('certifications')
        .select('*')
        .eq('content_id', eventId)
        .eq('enrollment_id', enrollmentId)
        .maybeSingle();

      if (existingCert) {
        setCertificate(existingCert);
        setHasAccess(true);
      } else {
        setCertificate(null);
        // Check ticket permissions
        const access = own?.config !== 'not_included';
        setHasAccess(access);

        if (!access) {
          // The "upgrade your ticket" CTA is only honest if some ticket on this
          // event actually grants a certificate. Without this check the card
          // offers an upgrade that opens a picker with no valid option in it -
          // the user ends up on "you cannot change to the same ticket type".
          const { data: certTickets } = await supabase
            .from('tickets')
            .select('id')
            .eq('content_id', eventId)
            .eq('allow_certificate', true)
            .not('certificate_template_id', 'is', null)
            .limit(1);

          setHasUpgradeTarget((certTickets?.length ?? 0) > 0);
          // Deliberately no early return: the no-access card claims the user has
          // met the requirements, so it needs the real requirement status to say
          // so. Returning here left `requirements` null, which read as "all met".
        }
      }

      // 2. Always fetch requirements (even if certificate exists)
      const requiresTest = await enrollmentRequiresTest(enrollmentId);
      const [testsResult, surveysResult] = await Promise.all([
        supabase
          .from('tests')
          .select('id, title, available_from, available_until, available_from_anchor, available_until_anchor')
          .eq('content_id', eventId)
          .eq('is_required', true)
          .eq('is_active', true),
        supabase
          .from('surveys')
          .select('id, title, survey_questions(archived_at)')
          .eq('content_id', eventId)
          .eq('is_required', true)
          .eq('is_active', true),
      ]);

      const requiredTests = requiresTest ? (testsResult.data || []) : [];
      // A disabled or question-less survey is not something a participant can
      // complete, so it must not count as an outstanding (or satisfied) requirement.
      const requiredSurveys = (surveysResult.data || []).filter(hasLiveQuestions);

      if (requiredTests.length > 0 || requiredSurveys.length > 0) {
        const [passedAttemptsResult, completedResponsesResult] = await Promise.all([
          requiredTests.length > 0
            ? supabase
                .from('test_attempts')
                .select('test_id')
                .eq('enrollment_id', enrollmentId)
                .eq('passed', true)
            : Promise.resolve({ data: [] as { test_id: string }[] }),
          requiredSurveys.length > 0
            ? supabase
                .from('survey_responses')
                .select('survey_id')
                .eq('enrollment_id', enrollmentId)
            : Promise.resolve({ data: [] as { survey_id: string }[] }),
        ]);

        const passedTestIds = new Set(
          (passedAttemptsResult.data || []).map((a) => a.test_id)
        );
        const completedSurveyIds = new Set(
          (completedResponsesResult.data || []).map((r) => r.survey_id)
        );

        // Anchored edges resolve against the event – the raw columns are stale
        // snapshots for them (see resolveWindow).
        const tests = requiredTests.map((test) => {
          const input: WindowInput = {
            from: test.available_from,
            from_anchor: test.available_from_anchor ?? 'absolute',
            until: test.available_until,
            until_anchor: test.available_until_anchor ?? 'absolute',
          };
          const resolved = resolveWindow(input, eventTimes);
          const state = windowStatus(input, eventTimes);
          return {
            title: test.title,
            passed: passedTestIds.has(test.id),
            expired: state === 'closed',
            upcoming: state === 'upcoming',
            opensAt: resolved.from ? resolved.from.toISOString() : null,
            closesAt: resolved.until ? resolved.until.toISOString() : null,
          };
        });
        const surveys = requiredSurveys.map((survey) => ({
          title: survey.title,
          completed: completedSurveyIds.has(survey.id),
        }));

        setRequirements({
          tests,
          surveys,
          allTestsPassed: tests.every((t) => t.passed),
          allSurveysCompleted: surveys.every((s) => s.completed),
        });
      } else {
        setRequirements(null);
      }
    } catch (err) {
      console.error('Error fetching certificate data:', err);
      // A transient failure during a silent poll keeps the last known state.
      if (!silent) setError(language === 'uk' ? 'Не вдалося завантажити дані' : 'Failed to load data');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [eventId, enrollmentId, eventEndDate, supabase, language]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  // ── Inline actions (issued certificates) ───────────────────────

  const trackEnrollment = (activity: string) => {
    fetch('/api/enrollment-tracking', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        enrollment_id: enrollmentId,
        milestone: 'certificate_obtained',
        activity,
      }),
    }).catch((err) => console.error('Failed to track:', err));
  };

  const handleView = () => {
    if (!certificate?.certificate_url) return;
    analytics.certificate.downloaded({
      certificate_id: certificate.id,
      content_id: eventId,
    });
    trackEnrollment('certificate_viewed');
    window.open(certificate.certificate_url, '_blank');
  };

  const handleDownload = async () => {
    if (!certificate?.certificate_url) return;
    analytics.certificate.downloaded({
      certificate_id: certificate.id,
      content_id: eventId,
    });
    trackEnrollment('certificate_viewed');

    try {
      setDownloading(true);
      const response = await fetch(certificate.certificate_url);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificate-${certificate.certificate_number || 'download'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed, opening in new tab:', err);
      window.open(certificate.certificate_url, '_blank');
    } finally {
      setDownloading(false);
    }
  };

  // ── Report wrong template ─────────────────────────────────────

  const handleOpenReportDialog = async () => {
    setReportDialogOpen(true);
    setReportStep('regenerate');

    if (!organizerInfo && !organizerLoading) {
      setOrganizerLoading(true);
      try {
        const { data: content } = await supabase
          .from('content')
          .select('organizer_id, organizers:organizer_profiles (name, email)')
          .eq('id', eventId)
          .single();

        const organizer = content?.organizers as { name: string; email: string } | null;
        if (organizer?.email) {
          setOrganizerInfo({
            name: organizer.name || organizer.email,
            email: organizer.email,
          });
        }
      } catch (err) {
        console.error('Error fetching organizer info:', err);
      } finally {
        setOrganizerLoading(false);
      }
    }
  };

  const handleConfirmReport = async () => {
    try {
      setReportSending(true);
      const response = await fetch('/api/certificates/report-template', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ courseId: eventId, courseName: eventTitle }),
      });

      if (response.ok) {
        toast.success(t('certificates.reportSent'));
        setReportDialogOpen(false);
      } else {
        toast.error(t('certificates.reportFailed'));
      }
    } catch (err) {
      console.error('Error reporting template:', err);
      toast.error(t('certificates.reportFailed'));
    } finally {
      setReportSending(false);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────

  const getTypeLabel = (type: CertificateType) => {
    const labels: Record<CertificateType, { uk: string; en: string }> = {
      participant: { uk: 'учасника', en: 'Participant' },
      speaker: { uk: 'спікера', en: 'Speaker' },
    };
    return language === 'uk' ? labels[type].uk : labels[type].en;
  };

  const getTypeLabelCapitalized = (type: CertificateType) => {
    const labels: Record<CertificateType, { uk: string; en: string }> = {
      participant: { uk: 'Учасник', en: 'Participant' },
      speaker: { uk: 'Спікер', en: 'Speaker' },
    };
    return language === 'uk' ? labels[type].uk : labels[type].en;
  };

  // ── Derive card state ─────────────────────────────────────────

  const allRequirementsMet = !requirements ||
    (requirements.allTestsPassed && requirements.allSurveysCompleted);
  const allTestsExpired = requirements
    ? requirements.tests.length > 0 && requirements.tests.filter(t => !t.passed).every(t => t.expired)
    : false;

  type CardState =
    | 'loading' | 'error' | 'no-access'
    | 'future' | 'future-pending'
    | 'not-configured'
    | 'requirements-incomplete' | 'requirements-expired'
    | 'ready-to-generate'
    | 'processing'
    | 'issued-with-pdf' | 'issued-no-pdf'
    | 'pending-past'
    | 'delayed' | 'failed';

  const getCardState = (): CardState => {
    if (loading) return 'loading';
    if (error) return 'error';
    if (hasAccess === false) return 'no-access';

    if (certificate) {
      if (certificate.status === 'issued' && certificate.certificate_url) return 'issued-with-pdf';
      if (certificate.status === 'issued') return 'issued-no-pdf';
      if (certificate.status === 'processing') {
        if (status?.render === 'delayed') return 'delayed';
        if (status?.render === 'failed') return 'failed';
        return 'processing';
      }
    }

    // Before any pending state: a pending row on a template-less ticket must
    // never show «Готовий» and a button that fails.
    if (status?.config === 'not_configured') return 'not-configured';

    if (certificate?.status === 'pending' && eventState !== 'past') return 'future-pending';
    if (certificate?.status === 'pending' && eventState === 'past') return 'pending-past';

    if (eventState !== 'past') return 'future';
    if (!allRequirementsMet) {
      return allTestsExpired ? 'requirements-expired' : 'requirements-incomplete';
    }
    return 'ready-to-generate';
  };

  const cardState = getCardState();

  // The card itself follows a render to its end, so leaving and coming back
  // (or simply waiting) shows the true state rather than a frozen spinner.
  useEffect(() => {
    if (cardState !== 'processing' && cardState !== 'delayed') return;
    const timer = setInterval(() => { fetchData(true); }, 10_000);
    return () => clearInterval(timer);
  }, [cardState, fetchData]);

  // A required test opening or closing while the card is open changes what the
  // checklist says; refetch at that moment instead of waiting for a reload.
  useTimeBoundaries(
    requirements?.tests.flatMap((test) => [test.opensAt, test.closesAt]) ?? [],
    () => { fetchData(true); },
  );

  // The test may have been passed in another tab. One silent refetch when the
  // participant comes back – only while there is still something to wait for.
  const awaitingRequirements =
    cardState === 'requirements-incomplete' || cardState === 'requirements-expired';
  useEffect(() => {
    if (!awaitingRequirements) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchData(true);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [awaitingRequirements, fetchData]);

  // Tell whoever can fix it (server re-verifies and throttles to once a day).
  useEffect(() => {
    if (cardState !== 'not-configured') return;
    fetch('/api/certificates/not-configured-notice', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contentId: eventId }),
    }).catch(() => undefined);
  }, [cardState, eventId]);

  const testOutstanding = !!requirements?.tests.some((test) => !test.passed && !test.expired);

  // Status badge config
  const getBadge = (): { text: string; color: string } => {
    switch (cardState) {
      case 'future':
      case 'loading':
        return { text: language === 'uk' ? 'Після події' : 'After event', color: 'bg-gray-100 text-gray-700' };
      case 'not-configured':
        return { text: language === 'uk' ? 'Не налаштовано' : 'Not set up', color: 'bg-amber-100 text-amber-700' };
      case 'requirements-incomplete':
        // A test still to take is the usual case – name it rather than a
        // generic "action needed".
        return testOutstanding
          ? { text: language === 'uk' ? 'Потрібен тест' : 'Test required', color: 'bg-blue-50 text-blue-700' }
          : { text: language === 'uk' ? 'Потрібні дії' : 'Action needed', color: 'bg-amber-100 text-amber-700' };
      case 'requirements-expired':
        return { text: language === 'uk' ? 'Недоступний' : 'Unavailable', color: 'bg-gray-100 text-gray-700' };
      case 'no-access':
        return { text: language === 'uk' ? 'Недоступний' : 'Unavailable', color: 'bg-gray-100 text-gray-700' };
      case 'ready-to-generate':
        return { text: language === 'uk' ? 'Готовий' : 'Ready', color: 'bg-green-100 text-green-700' };
      case 'future-pending':
        return { text: language === 'uk' ? 'Здобуто' : 'Earned', color: 'bg-blue-100 text-blue-700' };
      case 'processing':
        return { text: language === 'uk' ? 'Генерація' : 'Processing', color: 'bg-blue-100 text-blue-700' };
      case 'delayed':
        return { text: language === 'uk' ? 'Затримується' : 'Delayed', color: 'bg-amber-100 text-amber-700' };
      case 'failed':
        return { text: language === 'uk' ? 'Помилка' : 'Failed', color: 'bg-red-100 text-red-700' };
      case 'pending-past':
        return { text: language === 'uk' ? 'Готовий' : 'Ready', color: 'bg-green-100 text-green-700' };
      case 'issued-with-pdf':
      case 'issued-no-pdf':
        return { text: language === 'uk' ? 'Видано' : 'Issued', color: 'bg-green-100 text-green-700' };
      default:
        return { text: '', color: 'bg-gray-100 text-gray-700' };
    }
  };

  const badge = getBadge();

  // ── Render ─────────────────────────────────────────────────────

  // Loading skeleton inside card frame
  if (cardState === 'loading') {
    return (
      <div className="bg-white rounded-xl border">
        <div className="p-4 sm:p-5 animate-pulse">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="h-5 w-5 bg-gray-200 rounded" />
              <div className="h-5 w-32 bg-gray-200 rounded" />
            </div>
            <div className="h-5 w-20 bg-gray-200 rounded-full" />
          </div>
          <div className="h-4 w-3/4 bg-gray-100 rounded mb-2" />
          <div className="h-4 w-1/2 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border">
      {/* A. Card Header */}
      <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <Award className="h-5 w-5 text-blue-600 flex-shrink-0" />
          <h3 className="text-base font-semibold text-gray-900">
            {language === 'uk'
              ? `Сертифікат ${getTypeLabel(selectedType)}`
              : `${getTypeLabelCapitalized(selectedType)} Certificate`}
          </h3>
        </div>
        {cardState !== 'error' && (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${badge.color}`}>
            {badge.text}
          </span>
        )}
      </div>

      <div className="p-4 sm:p-5 space-y-4">
        {/* C. Requirements Checklist */}
        {requirements && (requirements.tests.length > 0 || requirements.surveys.length > 0) && cardState !== 'no-access' && cardState !== 'error' && (
          <div className="space-y-1.5">
            {/* Calm checklist: a test not taken yet is a step still ahead, not a
                failure – grey, never red. */}
            {requirements.tests.map((test, idx) => (
              <div key={`test-${idx}`} className="flex items-center justify-between gap-2">
                <div className={`flex items-center gap-2 min-w-0 ${test.passed ? 'text-green-600' : 'text-gray-600'}`}>
                  {test.passed ? (
                    <CheckCircle className="h-4 w-4 flex-shrink-0" />
                  ) : test.expired ? (
                    <Lock className="h-4 w-4 flex-shrink-0 text-gray-400" />
                  ) : test.upcoming ? (
                    <Clock className="h-4 w-4 flex-shrink-0 text-gray-400" />
                  ) : (
                    <Circle className="h-4 w-4 flex-shrink-0 text-gray-400" />
                  )}
                  <span className="text-sm truncate">
                    {test.title}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {test.passed ? (
                    <span className="text-xs text-gray-400">{language === 'uk' ? 'пройдено' : 'passed'}</span>
                  ) : test.expired ? (
                    <span className="text-xs text-gray-500">{language === 'uk' ? 'тест закрито' : 'test closed'}</span>
                  ) : test.upcoming ? (
                    <span className="text-xs text-gray-500">
                      {test.opensAt
                        ? (language === 'uk'
                          ? `відкриється ${formatShortDateTime(test.opensAt, 'о')}`
                          : `opens ${formatShortDateTime(test.opensAt, 'at')}`)
                        : (language === 'uk' ? 'ще не відкрито' : 'not open yet')}
                    </span>
                  ) : (
                    <>
                      <span className="text-xs text-gray-500">{language === 'uk' ? 'ще не пройдено' : 'not taken yet'}</span>
                      <button
                        onClick={() => document.getElementById('tests-section')?.scrollIntoView({ behavior: 'smooth' })}
                        className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-0.5"
                      >
                        {language === 'uk' ? 'Пройти' : 'Take'}
                        <ChevronRight className="h-3 w-3" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
            {requirements.surveys.map((survey, idx) => (
              <div key={`survey-${idx}`} className="flex items-center justify-between gap-2">
                <div className={`flex items-center gap-2 min-w-0 ${survey.completed ? 'text-green-600' : 'text-amber-600'}`}>
                  {survey.completed ? (
                    <CheckCircle className="h-4 w-4 flex-shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 flex-shrink-0" />
                  )}
                  <span className="text-sm truncate">
                    {survey.title}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {survey.completed ? (
                    <span className="text-xs text-gray-400">{language === 'uk' ? 'завершено' : 'completed'}</span>
                  ) : (
                    <>
                      <span className="text-xs text-amber-600">{language === 'uk' ? 'не завершено' : 'not completed'}</span>
                      <button
                        onClick={() => document.getElementById('surveys-section')?.scrollIntoView({ behavior: 'smooth' })}
                        className="text-xs text-gray-500 hover:text-blue-600 flex items-center gap-0.5"
                      >
                        <ChevronRight className="h-3 w-3" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* D. Body Content */}
        {cardState === 'error' && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-red-800">{error}</p>
                <Button variant="ghost" size="sm" onClick={() => fetchData()} className="mt-2 text-xs">
                  {language === 'uk' ? 'Спробувати ще раз' : 'Try again'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {cardState === 'no-access' && (
          isWithinUpgradeWindow(eventEndDate) && hasUpgradeTarget ? (
            <div className="space-y-3">
              {/* Only claim the requirements were met when there actually were
                  requirements and the user actually met them. This block used to
                  render unconditionally, telling participants they had passed a
                  test on events that have no tests at all. */}
              {requirements && allRequirementsMet && (
                <div className="rounded-lg bg-green-50 border border-green-200 p-3 flex items-start gap-2.5">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-green-900">{t('certificate.knowledgeChecked')}</p>
                    <p className="text-green-800 mt-0.5">{t('certificate.knowledgeCheckedDescription')}</p>
                  </div>
                </div>
              )}
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-start gap-2.5">
                <Award className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-900">{t('certificate.certificateInPremium')}</p>
                  <p className="text-amber-800 mt-0.5">{t('certificate.certificateInPremiumDescription')}</p>
                </div>
              </div>
              {onUpgradeTicket && (
                <Button
                  onClick={onUpgradeTicket}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Award className="h-4 w-4 mr-2" />
                  {t('certificate.upgradeTicketForCertificate')}
                </Button>
              )}
              {(() => {
                const deadline = getUpgradeDeadline(eventEndDate);
                if (!deadline) return null;
                return (
                  <p className="text-xs text-gray-500 text-center">
                    {t('certificate.upgradeAvailableUntil', { deadline: formatDateTimeEuropean(deadline) })}
                  </p>
                );
              })()}
            </div>
          ) : (
            <p className="text-sm text-gray-600">
              {/* Pointing at a "premium ticket" is only meaningful if one exists. */}
              {hasUpgradeTarget
                ? t('certificate.certificateInPremium')
                : t('certificate.notOfferedForThisEvent')}
            </p>
          )
        )}

        {cardState === 'future' && (
          <p className="text-sm text-gray-500">
            {t('certificate.availableAfterEvent')}
          </p>
        )}

        {cardState === 'future-pending' && (
          <p className="text-sm text-gray-500">
            {t('certificate.certificateEligible')}. {t('certificate.pdfAvailableAfterEvent')}
          </p>
        )}

        {cardState === 'not-configured' && (
          <p className="text-sm text-gray-500">
            {t('certificate.notConfiguredNotified')}
          </p>
        )}

        {cardState === 'requirements-expired' && (
          <p className="text-sm text-gray-600">
            {t('certificate.certificateUnavailable')}
          </p>
        )}

        {/* requirements-incomplete: checklist above tells the story, no extra text */}

        {cardState === 'processing' && (
          <div className="flex items-start gap-2 text-sm text-blue-600">
            <Loader className="h-4 w-4 flex-shrink-0 mt-0.5 animate-spin" />
            {t('certificate.renderGenerating')}
          </div>
        )}

        {cardState === 'delayed' && (
          <div className="flex items-start gap-2 text-sm text-amber-700">
            <Clock className="h-4 w-4 flex-shrink-0 mt-0.5" />
            {t('certificate.renderDelayed')}
          </div>
        )}

        {cardState === 'failed' && (
          <div className="space-y-2">
            <div className="flex items-start gap-2 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              {t('certificate.renderFailed')}
            </div>
            <Button onClick={() => onOpenCertificateModal(selectedType)} size="sm" variant="outline">
              <RefreshCw className="h-4 w-4 mr-1.5" />
              {t('certificate.retryGeneration')}
            </Button>
          </div>
        )}

        {(cardState === 'ready-to-generate' || cardState === 'pending-past') && (
          <Button
            onClick={() => onOpenCertificateModal(selectedType)}
            className="bg-blue-600 hover:bg-blue-700"
            size="sm"
          >
            <Award className="h-4 w-4 mr-1.5" />
            {t('certificate.getCertificate') || t('certificate.generateCertificate')}
          </Button>
        )}

        {cardState === 'issued-no-pdf' && (
          <Button
            onClick={() => onOpenCertificateModal(selectedType)}
            size="sm"
          >
            <RefreshCw className="h-4 w-4 mr-1.5" />
            {t('profile.certificates.generatePdf')}
          </Button>
        )}

        {cardState === 'issued-with-pdf' && (
          <div>
            {/* Certificate details */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-4 text-sm text-gray-500 mb-3">
              {certificate!.certificate_number && (
                <span className="inline-flex items-center gap-1.5">
                  <Award className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                  <span className="font-mono text-xs break-all">{certificate!.certificate_number}</span>
                </span>
              )}
              {certificate!.issued_at && (
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                  {new Date(certificate!.issued_at).toLocaleDateString(
                    language === 'uk' ? 'uk-UA' : 'en-US',
                    { year: 'numeric', month: 'long', day: 'numeric' }
                  )}
                </span>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="outline" size="sm" onClick={handleView}>
                <Eye className="h-3.5 w-3.5 mr-1.5" />
                {t('profile.certificates.view')}
              </Button>
              <Button
                size="sm"
                onClick={handleDownload}
                disabled={downloading}
                className="bg-green-600 hover:bg-green-700"
              >
                {downloading ? (
                  <Loader className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5 mr-1.5" />
                )}
                {t('profile.certificates.download')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                onClick={() => onOpenCertificateModal(selectedType, true)}
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                {t('profile.certificates.regenerate')}
              </Button>
            </div>
          </div>
        )}

        {/* E. Report Wrong Template */}
        {(cardState === 'issued-with-pdf' || cardState === 'issued-no-pdf') && (
          <div>
            {!reportDialogOpen ? (
              <button
                onClick={handleOpenReportDialog}
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                <MessageCircle className="h-4 w-4" />
                {t('certificates.reportWrongTemplate')}
              </button>
            ) : (
              <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 sm:p-5 space-y-3">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2 text-sm">
                  <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                  {t('certificates.reportDialogTitle')}
                </h4>

                {reportStep === 'regenerate' ? (
                  <>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {t('certificates.reportDialogRegenerateHint')}
                    </p>

                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        size="sm"
                        onClick={() => {
                          setReportDialogOpen(false);
                          onOpenCertificateModal(selectedType, true);
                        }}
                      >
                        <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                        {t('certificates.reportDialogRegenerateBtn')}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setReportDialogOpen(false)}
                        size="sm"
                      >
                        {t('certificates.reportDialogCancel')}
                      </Button>
                    </div>

                    <button
                      onClick={() => setReportStep('notify')}
                      className="text-sm text-gray-500 hover:text-blue-600 underline transition-colors"
                    >
                      {t('certificates.reportDialogAlreadyRegenerated')}
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {t('certificates.reportDialogBody')}
                    </p>

                    <p className="text-sm text-gray-700 leading-relaxed">
                      {t('certificates.reportDialogCta')}
                    </p>

                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        onClick={handleConfirmReport}
                        disabled={reportSending}
                        size="sm"
                      >
                        {reportSending ? (
                          <>
                            <Loader className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                            {t('certificates.reportSending')}
                          </>
                        ) : (
                          <>
                            <Mail className="h-3.5 w-3.5 mr-1.5" />
                            {t('certificates.reportDialogConfirm')}
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setReportDialogOpen(false)}
                        disabled={reportSending}
                        size="sm"
                      >
                        {t('certificates.reportDialogCancel')}
                      </Button>
                    </div>

                    {/* Organizer contact info */}
                    <div className="border-t border-amber-200/60 pt-3">
                      <p className="text-xs text-gray-500 mb-2">
                        {t('certificates.reportDialogContactAlt')}
                      </p>
                      {organizerLoading ? (
                        <p className="text-xs text-gray-400">{t('certificates.reportDialogLoadingOrganizer')}</p>
                      ) : organizerInfo ? (
                        <div className="flex items-start gap-2 text-sm">
                          <User className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-gray-800">{organizerInfo.name}</p>
                            <a
                              href={`mailto:${organizerInfo.email}`}
                              className="text-blue-600 hover:text-blue-800 hover:underline text-sm"
                            >
                              {organizerInfo.email}
                            </a>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
